import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentTenant } from "@/hooks/useTenants";

export interface TenantFeatureAccess {
  featureCode: string;
  enabled: boolean;
  featureName: string;
  category: string;
}

/**
 * Hook to fetch all tenant features for the current tenant
 */
export const useCurrentTenantFeatures = () => {
  const { currentTenantId } = useCurrentTenant();

  return useQuery({
    queryKey: ["current-tenant-features", currentTenantId],
    queryFn: async (): Promise<TenantFeatureAccess[]> => {
      if (!currentTenantId) return [];

      const { data, error } = await supabase
        .from("tenant_features")
        .select(`
          enabled,
          feature:platform_features(
            code,
            name,
            category,
            is_active
          )
        `)
        .eq("tenant_id", currentTenantId);

      if (error) throw error;

      // Transform and filter to only active features
      return (data || [])
        .filter((tf: any) => tf.feature?.is_active)
        .map((tf: any) => ({
          featureCode: tf.feature.code,
          enabled: tf.enabled,
          featureName: tf.feature.name,
          category: tf.feature.category,
        }));
    },
    enabled: !!currentTenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to check if the current tenant has a specific feature enabled
 * @param featureCode - The code of the feature to check (e.g., 'appointments', 'billing', 'invisalign')
 */
export const useTenantFeatureAccess = (featureCode: string) => {
  const { data: tenantFeatures, isLoading } = useCurrentTenantFeatures();

  const feature = tenantFeatures?.find((tf) => tf.featureCode === featureCode);
  const hasFeature = feature?.enabled ?? false;

  return {
    hasFeature,
    isLoading,
    featureName: feature?.featureName,
    category: feature?.category,
  };
};

/**
 * Hook to check multiple features at once
 * @param featureCodes - Array of feature codes to check
 */
export const useMultipleTenantFeatures = (featureCodes: string[]) => {
  const { data: tenantFeatures, isLoading } = useCurrentTenantFeatures();

  const featureAccess = featureCodes.reduce(
    (acc, code) => {
      const feature = tenantFeatures?.find((tf) => tf.featureCode === code);
      acc[code] = feature?.enabled ?? false;
      return acc;
    },
    {} as Record<string, boolean>
  );

  return {
    featureAccess,
    isLoading,
    hasAllFeatures: featureCodes.every((code) => featureAccess[code]),
    hasAnyFeature: featureCodes.some((code) => featureAccess[code]),
  };
};

// Mapping of feature codes to module types for integration
export const FEATURE_TO_MODULE_MAP: Record<string, string[]> = {
  appointments: ["appointments"],
  patients: ["patients"],
  billing: ["billing", "emi_tracking", "dues_tracker"],
  treatment_plans: ["treatment_planning", "treatment_tracking"],
  inventory: ["inventory", "vendor_invoice_management"],
  staff: ["assistant_logbook", "consultant_logbook"],
  reports: ["reports", "financial_audit", "clinical_audit"],
  clinical_notes: ["clinical_notes"],
  advanced_analytics: ["advanced_analytics"],
  invisalign: ["invisalign_tracking"],
  implants: ["implant_tracking"],
  braces: ["braces_tracking"],
  sms_notifications: [],
  whatsapp_integration: [],
  lab_integration: ["cases"],
  api_access: [],
  // Professional tier features
  radiology: ["radiology"],
  digital_consent: ["consent_forms"],
  staff_scheduling: ["staff_scheduling"],
  travel_reimbursement: ["travel_reimbursement"],
  priority_support: [],
  // Enterprise tier features
  multi_clinic_dashboard: ["multi_clinic_dashboard"],
  cross_clinic_analytics: ["cross_clinic_analytics"],
  hr_module: ["hr_module"],
  owner_pl_dashboard: ["owner_pl_dashboard"],
};

// Reverse mapping: module type to feature code
export const MODULE_TO_FEATURE_MAP: Record<string, string> = Object.entries(
  FEATURE_TO_MODULE_MAP
).reduce(
  (acc, [featureCode, moduleTypes]) => {
    moduleTypes.forEach((moduleType) => {
      acc[moduleType] = featureCode;
    });
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Hook to check if a module is enabled for the tenant based on feature flags
 * @param moduleType - The module type to check
 */
export const useModuleFeatureAccess = (moduleType: string) => {
  const { currentTenant, isLoading: tenantLoading } = useCurrentTenant();
  const featureCode = MODULE_TO_FEATURE_MAP[moduleType];
  const { hasFeature, isLoading: featureLoading } = useTenantFeatureAccess(featureCode || "");

  // If no feature mapping exists, the module is considered always accessible
  if (!featureCode) {
    return { hasModuleFeature: true, isLoading: false };
  }

  // If user has no tenant assignment, allow access based on user_module_permissions only
  // This ensures backward compatibility while tenant migration is in progress
  if (!currentTenant && !tenantLoading) {
    return { hasModuleFeature: true, isLoading: false };
  }

  return { hasModuleFeature: hasFeature, isLoading: tenantLoading || featureLoading };
};
